import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Globe,
  Pause,
  Archive,
  Trash2,
  MoreHorizontal,
  Eye,
  Copy,
  AlertTriangle,
} from 'lucide-react'
import { jobsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toaster'

type JobStatus = 'draft' | 'published' | 'paused' | 'closed'

interface JobActionsProps {
  jobId: string
  status: JobStatus
  onStatusChange?: () => void
}

export function JobActions({ jobId, status, onStatusChange }: JobActionsProps) {
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    action: 'publish' | 'pause' | 'close' | 'delete' | null
  }>({ open: false, action: null })

  const queryClient = useQueryClient()

  const publishJob = useMutation({
    mutationFn: () => jobsApi.publish(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      onStatusChange?.()
      toast({ title: 'Job published', description: 'The job is now visible on your career page', variant: 'success' })
      setConfirmDialog({ open: false, action: null })
    },
    onError: () => {
      toast({ title: 'Failed to publish job', variant: 'destructive' })
    },
  })

  const pauseJob = useMutation({
    mutationFn: () => jobsApi.pause(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      onStatusChange?.()
      toast({ title: 'Job paused', description: 'The job is no longer accepting applications', variant: 'success' })
      setConfirmDialog({ open: false, action: null })
    },
    onError: () => {
      toast({ title: 'Failed to pause job', variant: 'destructive' })
    },
  })

  const closeJob = useMutation({
    mutationFn: () => jobsApi.close(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      onStatusChange?.()
      toast({ title: 'Job closed', description: 'The position has been marked as closed', variant: 'success' })
      setConfirmDialog({ open: false, action: null })
    },
    onError: () => {
      toast({ title: 'Failed to close job', variant: 'destructive' })
    },
  })

  const deleteJob = useMutation({
    mutationFn: () => jobsApi.delete(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      onStatusChange?.()
      toast({ title: 'Job deleted', variant: 'success' })
      setConfirmDialog({ open: false, action: null })
    },
    onError: () => {
      toast({ title: 'Failed to delete job', variant: 'destructive' })
    },
  })

  const handleConfirmAction = () => {
    switch (confirmDialog.action) {
      case 'publish':
        publishJob.mutate()
        break
      case 'pause':
        pauseJob.mutate()
        break
      case 'close':
        closeJob.mutate()
        break
      case 'delete':
        deleteJob.mutate()
        break
    }
  }

  const isPending = publishJob.isPending || pauseJob.isPending || closeJob.isPending || deleteJob.isPending

  const getDialogContent = () => {
    switch (confirmDialog.action) {
      case 'publish':
        return {
          title: 'Publish Job',
          description: 'This will make the job visible on your career page and start accepting applications.',
          confirmLabel: 'Publish',
          variant: 'default' as const,
        }
      case 'pause':
        return {
          title: 'Pause Job',
          description: 'This will temporarily hide the job from your career page. You can resume it later.',
          confirmLabel: 'Pause',
          variant: 'default' as const,
        }
      case 'close':
        return {
          title: 'Close Job',
          description: 'This will mark the position as filled. The job will be archived and no longer accept applications.',
          confirmLabel: 'Close Job',
          variant: 'default' as const,
        }
      case 'delete':
        return {
          title: 'Delete Job',
          description: 'Are you sure you want to delete this job? This action cannot be undone.',
          confirmLabel: 'Delete',
          variant: 'destructive' as const,
        }
      default:
        return {
          title: '',
          description: '',
          confirmLabel: '',
          variant: 'default' as const,
        }
    }
  }

  const dialogContent = getDialogContent()

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Primary Action Button */}
        {status === 'draft' && (
          <Button onClick={() => setConfirmDialog({ open: true, action: 'publish' })}>
            <Globe className="h-4 w-4 mr-2" />
            Publish
          </Button>
        )}
        {status === 'published' && (
          <Button variant="outline" onClick={() => setConfirmDialog({ open: true, action: 'pause' })}>
            <Pause className="h-4 w-4 mr-2" />
            Pause
          </Button>
        )}
        {status === 'paused' && (
          <Button onClick={() => setConfirmDialog({ open: true, action: 'publish' })}>
            <Globe className="h-4 w-4 mr-2" />
            Resume
          </Button>
        )}

        {/* More Actions Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {status !== 'closed' && (
              <DropdownMenuItem onClick={() => setConfirmDialog({ open: true, action: 'close' })}>
                <Archive className="h-4 w-4 mr-2" />
                Close Job
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setConfirmDialog({ open: true, action: 'delete' })}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, action: confirmDialog.action })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog.action === 'delete' && <AlertTriangle className="h-5 w-5 text-destructive" />}
              {dialogContent.title}
            </DialogTitle>
            <DialogDescription>{dialogContent.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, action: null })}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant={dialogContent.variant}
              onClick={handleConfirmAction}
              loading={isPending}
            >
              {dialogContent.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
